import { Component, HostBinding, HostListener, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DOCUMENT } from '@angular/common';
import { IonContent, IonMenu, MenuController } from '@ionic/angular/standalone';
import { PuzzleScreenComponent } from 'projects/web/src/app/puzzle-screen/puzzle-screen.component'; // app-puzzle-screen
import { PuzzleSidenavComponent } from 'projects/web/src/app/puzzle-sidenav/puzzle-sidenav.component'; // app-puzzle-sidenav
import { buildEnv } from 'build-environment';
import { ThemeService } from '../../../tools/src/lib/shared-module/app-theme/theme.service';

export interface Tile {
  cols: number;
  rows: number;
  text: string;
}

const __BUILD_TIMESTAMP__: string = buildEnv.buildTimestamp;
const __BUILD_VERSION__: string = buildEnv.buildVersion;
const __CUSTOM_CSS__: string = buildEnv.customCss;
const __CUSTOM_JS__: string = buildEnv.customJs;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'krupion-puzzle-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [CommonModule, IonMenu, IonContent, PuzzleScreenComponent, PuzzleSidenavComponent],
})
export class AppComponent {
  prod = environment.production;

  private langToSet = 'de';
  removeSideMargins = false;

  // Classes set to app-root element. Can be set via puzzle config, to make puzzle specific theming possible.
  @HostBinding('class') puzzleClasses;

  constructor(
    private http: HttpClient,
    private meta: Meta,
    private title: Title,
    public framework: PuzzleFrameworkService,
    public leaderboard: LeaderboardService,
    private translate: TranslateService,
    private store: Store,
    @Inject(DOCUMENT) private doc,
    private menu: MenuController,
    private themeService: ThemeService // Needs to stay to initialize listeners
  ) {
    this.store
      .select((state) => state.puzzleGameState.language)
      .subscribe((langToSet) => {
        this.langToSet = langToSet;
        this.setLanguage(langToSet);
      });

    this.store
      .select((state) => state.puzzleFrameworkConfig.defaultGermanSalutation)
      .subscribe((defaultGermanSalutation) => {
        this.setLanguage(this.langToSet);
      });
    this.store
      .select((state) => state.puzzleFrameworkConfig.removeSideMargins)
      .subscribe((removeSideMargins) => {
        this.removeSideMargins = removeSideMargins;
      });

    this.framework.puzzleConfigLoaded$.subscribe((puzzleType) => {
      this.title.setTitle(this.framework.puzzleFrameworkConfig.title);
    });

    if (typeof __BUILD_TIMESTAMP__ !== 'undefined') {
      this.meta.addTag({ name: 'date-created', content: __BUILD_TIMESTAMP__ });
    }

    if (typeof __BUILD_VERSION__ !== 'undefined') {
      this.meta.addTag({ name: 'puzzle-version', content: __BUILD_VERSION__ });
    }

    // Add a link element to the head if the project contains custom css. __CUSTOM_CSS__ is set by webpack if a file exists.
    // Inspired by https://stackoverflow.com/a/50725256/4396362
    if (typeof __CUSTOM_CSS__ !== 'undefined' && __CUSTOM_CSS__) {
      const link: HTMLLinkElement = this.doc.createElement('link');
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('href', __CUSTOM_CSS__);
      this.doc.head.appendChild(link);
    }

    if (typeof __CUSTOM_JS__ !== 'undefined' && __CUSTOM_JS__) {
      const script: HTMLLinkElement = this.doc.createElement('script');
      script.setAttribute('src', __CUSTOM_JS__);
      this.doc.body.appendChild(script);
    }

    this.framework.initWebPuzzle();

    this.menu.enable(true, 'sidemenu');

    // Add the puzzle specific classes from the config file when the puzzle config is loaded.
    this.store
      .select((state) => state.puzzleFrameworkConfig.puzzleClasses)
      .subscribe((classes) => {
        this.puzzleClasses = classes;
      });
  }

  // @HostListener('touchmove', ['$event'])
  // preventIOSViewportZoom(event: KeyboardEvent) {
  //   // @TODO: find other solution
  //   event.preventDefault();
  // }
  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange(event) {
    document.documentElement.style.height = 'initial';
    window.setTimeout(() => {
      document.documentElement.style.height = '100%';
      window.setTimeout(() => {
        window.scrollTo(0, 1);
      }, 500);
    }, 500);
  }

  private async setLanguage(langToSet: string) {
    if (langToSet === 'de') {
      const defaultGermanSalutation = await this.store
        .selectOnce((state) => state.puzzleFrameworkConfig.defaultGermanSalutation)
        .toPromise();
      langToSet += '-' + defaultGermanSalutation || 'du';
    }
    this.translate.addLangs([langToSet]);
    this.translate.setDefaultLang(langToSet);
    this.translate.use(langToSet);
  }

  menuWillOpen() {
    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
  }

  menuDidClose() {
    this.framework.resumeCurrentGame();
  }
}
